import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, Alert, Stepper, Step, StepLabel, Card, CardContent } from "@mui/material";
import { TimezoneSelector } from "../TimezoneSelector";
import { WorkingHoursSelector } from "../WorkingHoursSelector";
import { userProfileService, UserProfile } from "../../services/UserProfileService";
import { createLogger } from "../../utils/logger";

const logger = createLogger("WorkingHoursStep");

interface WorkingHoursStepProps {
  onComplete: (data: {
    timezone: string;
    workingHours: {
      [key: string]: {
        start: string;
        end: string;
        enabled: boolean;
      };
    };
    bufferTime: number;
  }) => void;
  onBack?: () => void;
}

export const WorkingHoursStep: React.FC<WorkingHoursStepProps> = ({ onComplete, onBack }) => {
  const [timezone, setTimezone] = useState("");
  const [workingHours, setWorkingHours] = useState<{
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  }>({});
  const [bufferTime, setBufferTime] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing user profile data on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const profile = await userProfileService.getUserProfile();

        if (profile) {
          setTimezone(profile.timezone || "");
          setWorkingHours(profile.workingHours || {});
          setBufferTime(profile.bufferTime || 15);
        }
      } catch (error) {
        logger.debug("No existing profile found or error fetching:", error);
        // This is expected for new users, so we don't show an error
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleSaveWorkingHours = async () => {
    if (!timezone.trim()) {
      setError("Please select your timezone");
      return;
    }

    // Check if at least one working day is enabled
    const hasWorkingDays = Object.values(workingHours).some((day) => day.enabled);
    if (!hasWorkingDays) {
      setError("Please enable at least one working day");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save working hours to backend
      await userProfileService.updateUserProfile({
        timezone: timezone.trim(),
        workingHours,
        bufferTime,
      });

      // Pass the data to the parent component
      onComplete({
        timezone: timezone.trim(),
        workingHours,
        bufferTime,
      });
    } catch (error) {
      logger.error("Error saving working hours:", error);
      setError("Failed to save working hours. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // Set default values and continue
    const defaultWorkingHours = {
      monday: { start: "09:00", end: "17:00", enabled: true },
      tuesday: { start: "09:00", end: "17:00", enabled: true },
      wednesday: { start: "09:00", end: "17:00", enabled: true },
      thursday: { start: "09:00", end: "17:00", enabled: true },
      friday: { start: "09:00", end: "17:00", enabled: true },
      saturday: { start: "09:00", end: "17:00", enabled: false },
      sunday: { start: "09:00", end: "17:00", enabled: false },
    };

    onComplete({
      timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      workingHours: defaultWorkingHours,
      bufferTime: 15,
    });
  };

  // Show loading state while fetching profile
  if (isLoadingProfile) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Set Your Working Hours
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Help your AI assistant schedule meetings at the right times by setting your timezone and working hours.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Your Timezone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This helps us display times correctly and suggest meeting times in your local time.
            </Typography>
            <TimezoneSelector value={timezone} onChange={setTimezone} autoDetect={true} label="Select your timezone" helperText="We'll auto-detect your timezone, but you can change it if needed." />
          </Box>

          <Box sx={{ mb: 4 }}>
            <WorkingHoursSelector
              value={workingHours}
              onChange={setWorkingHours}
              bufferTime={bufferTime}
              onBufferTimeChange={setBufferTime}
              showBufferTime={true}
              helperText="Set your working hours for each day. You can copy Monday's hours to other days."
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button variant="outlined" onClick={onBack} disabled={isLoading}>
              Back
            </Button>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="text" onClick={handleSkip} disabled={isLoading}>
                Skip for now
              </Button>

              <Button variant="contained" onClick={handleSaveWorkingHours} disabled={isLoading || !timezone.trim()}>
                {isLoading ? <CircularProgress size={20} /> : "Continue"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
